import { MdDelete } from "react-icons/md";
import { Dropdown } from "primereact/dropdown";
import { Toast } from 'primereact/toast';
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useCart } from "../../contexts/CartProvider";
import { useToast } from "../../contexts/ToastContext";
import backofficeAxiosInstance from "../../api/backoffice.api";
import { useRCN } from "../../contexts/RCNContext";
import { useMenuAccess } from "../../contexts/MenuAccessContext";

export default function CartPanel() {
  const {RCN} = useRCN();
  const { permissions } = useMenuAccess();
  const {
    cartItems,
    showCart,
    setShowCart,
    removeFromCart,
    setCartId,
    cartId,
    updateCartItem,
    addToCart,
    clearCart,
  } = useCart();

  const toast = useRef(null);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isWholeSalesContext = location.pathname.includes('/whole-sales');
  const isPurchaseManagementContext = location.pathname.includes('/purchase-management');
  const searchParams = new URLSearchParams(location.search);
  const retailerIdFromQuery = searchParams.get("retailerId") || null;
  const editPoId = searchParams.get("edit-po-id") || "";
  const isPurchaseOrderEditMode = isPurchaseManagementContext && Boolean(editPoId);
  const [retailerLoading, setRetailerLoading] = useState(false);
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState(retailerIdFromQuery);

  // const { 
  //   addInvoice,
  //   addPurchaseOrder
  // } = useTransaction();

  // const updateCartItem = (id, key, newValue) => {
  //   // setCartItems(prevItems =>
  //   //   prevItems?.map(item =>
  //   //     item.id === id ? { ...item, [key]: newValue } : item
  //   //   )
  //   // );
  // };
  
  const clearCartItems = async () => {
    try {
      const response = await backofficeAxiosInstance.post('cart/clear',{ headers: { 'x-rcn': RCN } });
      if(response.status === 200){
        toast.current?.show({
          life: 2000,
          severity: 'success',
          summary: 'Success',
          detail: 'Clear cart'
        });
        clearCart();
      }
    } catch (error) {
       toast.current?.show({
        life: 2000,
        severity: 'error',
        summary: 'Failed',
        detail: 'Failed to clear cart'
      });
    }
  }

useEffect(() => {
  async function init() {
    try {
      const cartRes = await backofficeAxiosInstance.get('/cart/get');

      if (cartRes.status === 200){
        addToCart(cartRes?.data?.data?.items || []);
        setCartId(cartRes?.data?.data?.id)
      }

    } catch (error) {
      // console.error('Error fetching data in parallel:', error);
    }
  }

  init();
}, [permissions?.distribution]);

  const parseRetailerOptions = (response) => {
    const payload = response?.data;
    const list =
      (Array.isArray(payload?.data?.content) && payload.data.content) ||
      (Array.isArray(payload?.data) && payload.data) ||
      (Array.isArray(payload?.content) && payload.content) ||
      (Array.isArray(payload) && payload) ||
      [];

    return list
      .map((retailer) => {
        const id = retailer?.id ?? retailer?.retailerId ?? retailer?.value;
        if (id === null || id === undefined) return null;
        const storeName =
          retailer?.storeName ||
          retailer?.name ||
          retailer?.retailerName ||
          retailer?.label ||
          `Retailer ${id}`;
        return { label: storeName, value: id };
      })
      .filter(Boolean);
  };

  useEffect(() => {
    if (!showCart || !isWholeSalesContext || cartItems.length === 0) return;

    async function fetchRetailers() {
      try {
        setRetailerLoading(true);
        const response = await backofficeAxiosInstance.get("/retailers/all-merchantRetailers");
        const options = parseRetailerOptions(response);
        setRetailerOptions(options);
        setSelectedRetailerId((prev) => {
          if (retailerIdFromQuery && options.some((option) => String(option.value) === String(retailerIdFromQuery))) {
            return retailerIdFromQuery;
          }
          if (prev && options.some((option) => String(option.value) === String(prev))) {
            return prev;
          }
          return options[0]?.value || null;
        });
      } catch (error) {
        toast.current?.show({
          life: 2500,
          severity: "error",
          summary: "Retailers",
          detail: "Failed to fetch retailer list",
        });
      } finally {
        setRetailerLoading(false);
      }
    }

    fetchRetailers();
  }, [showCart, isWholeSalesContext, cartItems.length, retailerIdFromQuery]);


  const createPO = async () => {
    if (isWholeSalesContext && !selectedRetailerId) {
      addToast("Please select a retailer before creating the purchase order", "Retailer Required", "warn", 3000);
      return;
    }

    try {
      await updateCartItems();
      let createPoUrl = isWholeSalesContext
        ? `/purchaseorder/create-from-cart?retailerId=${encodeURIComponent(selectedRetailerId)}`
        : "/purchaseorder/create-from-cart";
      if (isPurchaseOrderEditMode) {
        createPoUrl = `/purchaseorder/create-from-cart?purchaseOrderId=${encodeURIComponent(editPoId)}`;
      }
      const response = await backofficeAxiosInstance.post(createPoUrl, {});
      const poIds = Array.isArray(response?.data?.data)
        ? response.data.data.filter(Boolean)
        : response?.data?.data
          ? [response.data.data]
          : [];
      const successMessage = poIds.length > 0
        ? `Purchase Order ${isPurchaseOrderEditMode ? "updated" : "created"} successfully: ${poIds.join(", ")}`
        : `Purchase Order ${isPurchaseOrderEditMode ? "updated" : "created"} successfully`;
      addToast(successMessage, "Success", "success", 3000);
      setShowCart(false);
      if (isWholeSalesContext) {
        navigate('/whole-sales/purchase-orders');
      } else if (isPurchaseManagementContext) {
        navigate('/purchase-management/purchase-orders');
      }
      clearCart();
    } catch (error) {
      console.error("Error creating POs:", error);
      addToast("Something went wrong while creating the Purchase Orders", "Error", "error", 4000);
    }
  };

  const handleAddMoreItems = () => {
    const queryParams = new URLSearchParams(location.search);
    const locationId = queryParams.get("id");
    const retailerId = queryParams.get("retailerId") || selectedRetailerId;
    const targetPath = isWholeSalesContext
      ? "/whole-sales/product-catalogs"
      : "/purchase-management/product-catalogs";
    const nextParams = new URLSearchParams();

    if (locationId) nextParams.set("id", locationId);
    if (isWholeSalesContext && retailerId) nextParams.set("retailerId", retailerId);
    if (isPurchaseManagementContext && editPoId) nextParams.set("edit-po-id", editPoId);

    setShowCart(false);
    navigate(nextParams.toString() ? `${targetPath}?${nextParams.toString()}` : targetPath);
  };

  const updateCartItems = async () => {
    const items = cartItems?.map((item) => ({ ...item }));
    const payload =
      isWholeSalesContext && cartId ? { id: cartId, items } : { items };
    await backofficeAxiosInstance.post("/cart/add", payload, {
      headers: { "x-rcn": RCN },
    });
  };

  const removeItemFromCart = async (itemId) => {
    try {
      const items = cartItems
        ?.filter((item) => item.itemId !== itemId)
        ?.map((item) => ({ ...item }));
      const payload =
        isWholeSalesContext && cartId ? { id: cartId, items } : { items };
      const response = await backofficeAxiosInstance.post("/cart/add", payload, {
        headers: { "x-rcn": RCN },
      });
      if(response.status === 200){
        toast.current?.show({
          life: 2000,
          severity: 'success',
          summary: 'Removed',
          detail: 'Removed item'
        });
      }
    } catch (error) {
      addToast("Something went wrong while removing item", "Error", "error", 4000);
    }
  };

  if (!showCart) return null;

  const getItemUnitPrice = (item) => {
    const price = Number(item?.unitPrice ?? item?.price ?? item?.cost ?? 0);
    return Number.isFinite(price) ? price : 0;
  };

  const subtotal = (cartItems || []).reduce((sum, item) => {
    const quantity = Number(item?.requestedQuantity);
    const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    return sum + safeQuantity * getItemUnitPrice(item);
  }, 0);

  const formatCurrency = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `$${numeric.toFixed(2)}` : "$0.00";
  };

  return (
    <div className="fixed top-0 right-0 w-full bg-[#00000080] bg-opacity-10 h-full z-50 overflow-y-auto">
      <div className="fixed top-0 right-0 w-[360px] max-w-full h-full bg-white shadow-lg z-50 overflow-y-auto">
        <Toast ref={toast} />
        <div className="sticky top-0 flex justify-between items-center px-4 py-3 border-b bg-gray-100">
          <h2 className="text-lg font-semibold">Cart</h2>
          <div className="flex items-center gap-4">
            {cartItems?.length > 0 && (
              <button
                onClick={clearCartItems}
                title="Clear all items"
                className="text-sm text-red-600 font-semibold underline">
                Clear
              </button>
            )}
            <button
              className="text-red font-semibold"
              onClick={() => setShowCart(false)}
            >
              Close
            </button>
          </div>
        </div>

        <div className="p-4 flex flex-col gap-10">
          {cartItems?.map((item) => (
            <div key={item.itemId} className="border flex rounded justify-between p-3 space-y-2">
              <div className="flex flex-col gap-2">
                <div className="text-sm font-medium">{item.itemName || "Unnamed Item"}</div>
                <div className="flex gap-3 items-center">
                  <span className="w-20">Qty:</span>
                  <input
                    type="number"
                    min={1}
                    value={item.requestedQuantity}
                    onChange={(e) =>
                      updateCartItem(item.itemId, "requestedQuantity", parseInt(e.target.value))
                    }
                    className="p-inputtext w-24 border border-gray-300 rounded"
                  />
                </div>
                <div className="flex gap-3 items-center">
                  <span className="w-20">Price:</span>
                  <input
                    type="number"
                    min={0}
                    disabled
                    value={getItemUnitPrice(item)}
                    onChange={(e) =>
                      updateCartItem(item.itemId, "unitPrice", parseFloat(e.target.value))
                    }
                    className="p-inputtext w-24 border border-gray-300 rounded"
                  />
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="w-20">Subtotal:</span>
                  <span className="font-medium">
                    {formatCurrency((Number(item?.requestedQuantity) || 0) * getItemUnitPrice(item))}
                  </span>
                </div>
              </div>
              <MdDelete
                size={18}
                onClick={ async () => {
                  removeFromCart(item?.itemId);
                  await removeItemFromCart(item?.itemId);
                }}
                className="text-primaryBlue cursor-pointer"
              />
            </div>
          ))}

          {cartItems?.length > 0 && (
            <div className="flex flex-wrap flex-col gap-5">
              {isWholeSalesContext && (
                <div className="flex flex-col gap-2">
                  <label className="font-medium">Retailer</label>
                  <Dropdown
                    filter
                    disabled={retailerLoading}
                    className="w-80 border"
                    value={selectedRetailerId}
                    options={retailerOptions}
                    onChange={(e) => setSelectedRetailerId(e.value)}
                    placeholder={retailerLoading ? "Loading retailers..." : "Select retailer"}
                  />
                </div>
              )}
              <div className="flex justify-between items-center border-t pt-3">
                <span className="font-semibold">Total</span>
                <span className="font-semibold">{formatCurrency(subtotal)}</span>
              </div>
              <button
                onClick={handleAddMoreItems}
                className="bg-white border border-primaryBlue text-primaryBlue p-2 w-60 rounded font-semibold">
                Add More Items
              </button>
              <button
                onClick={createPO}
                className="bg-primaryBlue text-white p-2 w-60 rounded font-semibold">
                {isPurchaseOrderEditMode ? "Update Purchase Order" : "Create Purchase Order"}
              </button>
            </div>
          )}

          {cartItems?.length === 0 && (
            <div className="text-center text-gray-500 py-10">No items in cart</div>
          )}
        </div>
      </div>
    </div>
  );
}
